import { AccountType } from './types'

// Both taken from the XM3 web header (clients/views/mixins.pug), so the new page keeps the
// same destinations as the pug signup it replaces.
export const HOME_URL = 'https://home.xtramath.org'
export const SIGN_IN_URL = '/#/signin/teacher'

// XM3 reads "r" on the sign-in screen and returns there afterwards (see clients screen.ts).
export const LICENSE_OVERVIEW_URL =
  '/#/signin/teacher?r=/teacher/license_overview'
export const ENROLL_URL = '/app/signup/family/enroll'
export const QUOTE_URL = 'https://quotes.xtramath.org/'

export const HEADING = 'Create your XtraMath account'
export const SUB_HEADING = 'What type of account do you need?'

// Shown when someone picks Student. Students do not create their own accounts.
export const STUDENT_NOTICE =
  "Students can't create an account on their own. A teacher or parent needs to set one up for you."
export const STUDENT_NOTICE_LINK = 'Already have an account? Sign In'

export const ACCOUNT_TYPES: AccountType[] = [
  {
    id: 'teacher',
    title: 'Teacher / Educator',
    description: 'Classroom teacher, math coach, or specialist',
    primary: { label: 'Create my account', to: 'teacher' },
    secondary: {
      label: 'I already have a Teacher Access Code',
      shortLabel: 'I have an access code',
      href: LICENSE_OVERVIEW_URL,
    },
    link: {
      label: 'Need pricing for your school? I need a quote',
      shortLabel: 'I need a quote',
      href: QUOTE_URL,
    },
  },
  {
    id: 'family',
    title: 'Parent / Family',
    description: "Supporting my child's math practice at home",
    primary: { label: 'Create my account', to: 'family' },
    secondary: { label: 'Enroll my child (I have a code)', href: ENROLL_URL },
  },
  {
    id: 'administrator',
    title: 'Administrator',
    description: 'Principal, school admin, or district leader',
    primary: { label: 'Next', to: 'administrator' },
  },
  {
    // A student cannot create their own account — a teacher or parent adds them. So this type
    // has no actions; the chooser shows StudentNotice instead.
    id: 'student',
    title: 'Student',
    description: "I'm a student looking to practice",
  },
]

// XM3 validates the address and rejects one already in use (api2-teacher onPreSignup).
export const PRE_SIGNUP_URL = '/api2/teacher/pre-signup'

export const SIGNUP_STEPS = ['Your info', 'Confirm', 'Choose plan']

export const MIN_PASSWORD_LENGTH = 8

// Every rule must pass before the form can continue. The label is the text shown to the user.
export const PASSWORD_RULES: {
  label: string
  test: (value: string) => boolean
}[] = [
  {
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (value) => value.length >= MIN_PASSWORD_LENGTH,
  },
  { label: 'One uppercase letter (A–Z)', test: (value) => /[A-Z]/.test(value) },
  { label: 'One number (0–9)', test: (value) => /[0-9]/.test(value) },
  {
    label: 'One special character (!@#$%...)',
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
]

export const TEACHER_COPY = {
  heading: 'Create your account',
  emailPlaceholder: 'e.g. sjohnson@school.edu',
  subHeading: 'Start with your basic information.',
  accountExists: 'An account already uses this email address.',
  invalidEmail: 'Enter a valid email address.',
  firstNameRequired: 'Enter your first name.',
  lastNameRequired: 'Enter your last name.',
  genericError: 'Something went wrong. Please try again.',
  ssoExpired: 'That sign-in took too long. Please try again.',
  ssoFailed: 'We could not sign you in with that provider. Please try again.',
  ssoAccountExistsCta: 'Sign in to continue',
}

// Both taken from XM3's shared footer (clients/views/shared-dom-objects.pug).
export const TERMS_URL = 'https://home.xtramath.org/terms-of-service'
export const PRIVACY_URL = 'https://home.xtramath.org/privacy'

// The pug form capped these; the server has no limit of its own.
export const MAX_NAME_LENGTH = 40
export const MAX_EMAIL_LENGTH = 128

export const CONFIRM_COPY = {
  heading: 'Confirm & agree',
  subHeading: 'One last step before we create your account.',
  privacyNotice:
    'XtraMath never sells data or shows ads. COPPA & FERPA compliant.',
  over16:
    'I am 16 years of age or older (or the parent/guardian setting up this account).',
  marketing: 'Send me tips and updates from XtraMath.',
  error: 'We could not create your account. Please try again.',
}

// Clears the flag that sends a teacher back into the signup wizard (api2-teacher onFinishSignup).
export const FINISH_SIGNUP_URL = '/api2/teacher/finish-signup'
// The same sales address the upgrade page uses.
export const TALK_TO_US_URL = 'mailto:sales@xtramath.org'

export const PLAN_COPY = {
  heading: 'Choose your plan',
  subHeading: 'Start free forever, or unlock more. You can change any time.',
  freeChip: 'ALWAYS FREE',
  freePlanName: 'Standard Plan — Family & Teacher only',
  freeDetail: 'No credit card · no commitment · since 2009',
  freeCta: 'Continue with free',
  divider: 'OR UPGRADE TO XTRAMATH PRO FOR MORE',
  anotherType: 'Need a different type of account?',
  anotherTypeCta: 'Select another account type',
  questions: 'Have any questions before you finish?',
  questionsCta: 'Talk to us',
}

export const WELCOME_COPY = {
  heading: 'Welcome to XtraMath Pro',
  subHeading:
    "Your students are about to feel the difference.\nYou've got everything you need to make math click — let's build something great.",
  renewedHeading: 'Your plan is renewed',
  renewedSubHeading:
    'Everything carries on exactly as it was — your classes, your students and your teachers all stay put.',
  renewedLabel: 'RENEWED',
  renewedUntilLabel: 'Renewed until',
  active: 'ACTIVE',
  pending: 'ACTIVATING',
  planLabel: 'Plan',
  paidLabel: 'Amount paid',
  renewalLabel: 'Next renewal',
  emailLabel: 'Confirmation sent to',
  receiptNote: 'A confirmation email with your receipt has been sent to',
  getStarted: 'GET STARTED',
  dashboardCta: 'Go to my dashboard',
  steps: [
    {
      title: 'Name your first class',
      detail: 'Name your class and select when your class will end.',
    },
    {
      title: 'Add your students',
      detail: 'Import a roster or add students one by one to your class.',
    },
    {
      title: 'Set their custom program',
      detail:
        'From the classroom view, in the side bar select Program settings and assign the right program for each student.',
    },
  ],
}

// A school larger than this is not priced on the page; Sales builds the plan.
export const SCHOOL_SEAT_CAP = 2000

export const SCHOOL_COPY = {
  heading: 'Set up XtraMath for your school',
  subHeading:
    'School and district plans require verification and are set up with our Sales team.',
  priceUnit: 'per student · year',
  included: 'all teachers and grades included',
  calculator: 'Calculate your school price',
  totalLabel: 'Total / year',
  quoteCta: 'Request a Quote',
  overCap: `More than ${SCHOOL_SEAT_CAP.toLocaleString(
    'en-US'
  )} students? Request a quote and Sales will build your plan.`,
}

export const ENROLL_COPY = {
  heading: 'Welcome to XtraMath',
  subHeading: 'Enroll to get started.',
  boxTitle: 'Enter an enrollment code',
  note: "Teachers send home flyers with enrollment codes. Codes are specific to individual students — please don't reuse them.",
  noCode: "If your child isn't using XtraMath at school,",
  noCodeCta: 'sign up for a parent account',
  emailLabel: "Parent's email",
  emailPlaceholder: 'e.g. sarah.johnson@gmail.com',
  codeLabel: 'Enrollment code',
  codePlaceholder: 'XXXX-XXXX-XXXX',
  setUpCta: 'Set up',
  signInCta: 'Already have an account? Sign in to link your child',
  signInCtaShort: 'Sign in to link your child',
  // Kept from the pug page: this is the only point an account that already exists passes through.
  consent:
    "I give my consent for XtraMath to use my child's data and my data in accordance with XtraMath's Terms of Service and Privacy Policy. XtraMath respects your privacy and does not sell your information to third parties.",
  consentRequired: 'Please check the box to provide your required consent.',
  codeNotFound:
    'That enrollment code was not found. Please check the flyer and try again.',
  alreadyEnrolled: 'That child is already enrolled. Sign in to see them.',
  genericError: 'Something went wrong. Please try again.',
}

// The notices the pug client raised as post-it popups while enrolling. Copy is taken from
// i18n/eng/client-language.xml, so both flows read the same.
export const ENROLL_NOTICES = {
  signup: {
    title: 'Sign up',
    message:
      'Please sign up to complete the enrollment process. If you already have an XtraMath parent account, please go back and verify your email address in the enrollment form.',
  },
  signin: {
    title: 'Sign in',
    message: 'Please sign in to complete the enrollment process.',
  },
  alreadyEnrolled: {
    title: 'Already enrolled',
    message:
      'The student is already enrolled in your parent account. Their name will be remembered on this device.',
  },
  restart: {
    title: 'Start again',
    message:
      'We lost track of the child to link. Please enter the enrollment code again.',
  },
}

// Choosing which of the parent's children the enrolled student is.
export const SELECT_CHILD_COPY = {
  heading: 'Select child',
  whichChild: (name: string) =>
    `Which child in your existing XtraMath account is ${name}?`,
  noneOfAbove: (name: string) =>
    `None of the above. Add ${name} to my account.`,
  submit: 'Next',
  specifyChild: 'Please select a student.',
  confirmTitle: 'Confirm selection',
  confirmMessage: (a: string, b: string) =>
    `Are ${a} and ${b} two names for the same student?`,
  confirmNote:
    'If they are not please select another option, probably None of the above.',
  confirmCta: 'OK',
  cancelCta: 'Cancel',
}

// Shown once a child is enrolled, before the student signs in for the first time. Copy follows
// XM3's #enrollConfirmationPopup, except for the last sentence: that popup said the details had
// been emailed, which is not true. The add-child and enroll-confirmation templates carry only the
// child's name, so this says only what the server actually sends.
export const ENROLLED_COPY = {
  heading: 'Success!',
  body: "You've successfully enrolled in XtraMath! Please write this information down so you can sign in anywhere. We've also sent you a confirmation email.",
  emailLabel: 'PARENT EMAIL ADDRESS',
  nameLabel: 'STUDENT NAME',
  pinLabel: 'STUDENT PIN',
  cta: 'Go to student sign in',
}

// Linking a provider to the account that already owns the email. Copy follows XM3's
// finishSsoMessages.linkTeacherPrompt.
export const LINK_COPY = {
  heading: 'Link your account',
  linkPrompt: (provider: string) =>
    `Sign in to your XtraMath family, teacher or administrator account to link it with ${provider}. You will only need to do this once.`,
  forgotPassword: 'Forgot your password?',
}

// Signing in to link an enrolled child to an account that already exists.
export const ENROLL_SIGN_IN_COPY = {
  heading: 'Sign in to link your child',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  submit: 'Sign in',
  failed: 'That email and password do not match an account.',
}

// Where a child signs in, once they are on their parent's account.
export const STUDENT_SIGN_IN_URL = '/#/signin/student'

// The parent flow is the teacher flow with different wording. Only what differs is restated.
export const FAMILY_COPY = {
  ...TEACHER_COPY,
  heading: 'Tell us about you',
  subHeading: 'Start with your basic information.',
  emailPlaceholder: 'e.g. sarah.johnson@gmail.com',
  // A parent has no school licence to find, so nothing is asked of their address.
}

// A parent signs in with Google alone; Clever and ClassLink are school accounts.
export const FAMILY_SSO_PROVIDERS = ['google'] as const

export const FAMILY_PLAN_COPY = {
  freeNote: 'Free includes 1 child · upgrade any time',
  panelName: 'Family Plan',
  cta: 'Get Family Plan',
  childrenLabel: 'Children in your account',
  includedLabel: 'Children included',
  // A family plan has no extra seats to buy, so a bigger family talks to Sales.
  needMore: 'Need more?',
  needMoreCta: 'Contact Sales',
}

export const ACCOUNT_COPY = {
  teacher: TEACHER_COPY,
  family: FAMILY_COPY,
}
